/**
 * Modal for sending kudos to a teammate.
 *
 * Triggered from UserPublicProfileModal (and the recognition page in the
 * future). The `toUser` ref is supplied by the caller — we don't expose a
 * picker here since it's used after the user's profile is already on screen.
 *
 * On success: toast confirmation + close the modal. The slice handles
 * snapshot + feed state updates from the response.
 */
import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { Button } from "@shopify/polaris";
import { toast } from "sonner";
import UserAvatar from "components/user-avatar/user-avatar";
import { setIsModalxOpen } from "components/modalx/modalx.slice";
import useIsPhone from "features/files/use-is-phone";
import { AppDispatch } from "configureStore";
import { useAppSelector } from "hooks";
import {
  fetchSnapshotThunk,
  giveKudosThunk,
} from "features/recognition/recognition.slice";
import { PointsUserRef } from "types/Points";

interface Props {
  companyId: string;
  toUser: PointsUserRef;
}

const MIN_REASON = 3;
const MAX_REASON = 280;

const GiveKudosModal: React.FC<Props> = ({ companyId, toUser }) => {
  const dispatch: AppDispatch = useDispatch();
  const { t } = useTranslation();
  const isPhone = useIsPhone();
  const { snapshot, snapshotCompanyId, giving } = useAppSelector(
    (state) => state.recognitionReducer
  );
  const [reason, setReason] = useState("");
  const [amount, setAmount] = useState(1);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Make sure the cached balance matches the company we're inside. The
  // recognition page primes this on mount, but the modal can also be opened
  // directly from the profile flow on a fresh tab.
  useEffect(() => {
    if (snapshotCompanyId !== companyId) {
      dispatch(fetchSnapshotThunk(companyId));
    }
  }, [companyId, snapshotCompanyId, dispatch]);

  const balance = snapshotCompanyId === companyId ? snapshot?.giveBalance ?? 0 : 0;
  const trimmedLen = reason.trim().length;
  const reasonOk = trimmedLen >= MIN_REASON && trimmedLen <= MAX_REASON;
  const amountOk = Number.isInteger(amount) && amount >= 1 && amount <= balance;
  const canSubmit = reasonOk && amountOk && balance > 0 && !giving;

  // Clamp the chosen amount when balance changes (e.g., snapshot loads or
  // a successful give() reduced the balance below the picker value).
  useEffect(() => {
    if (balance < 1) {
      setAmount(1);
      return;
    }
    if (amount > balance) setAmount(balance);
  }, [balance, amount]);

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitError(null);
    const result = await dispatch(
      giveKudosThunk({ companyId, toUserId: toUser.id, reason: reason.trim(), amount })
    );
    if (giveKudosThunk.fulfilled.match(result)) {
      toast.success(t("kudos_sent", { defaultValue: "Kudos sent" }));
      dispatch(setIsModalxOpen(false));
    } else {
      const payload = result.payload as { error?: string; code?: string } | undefined;
      const code = payload?.code;
      // Map server-side codes to friendlier messages where useful.
      const message =
        code === "NO_KUDOS_LEFT"
          ? t("kudos_no_balance", { defaultValue: "No kudos left for today" })
          : code === "REASON_TOO_SHORT"
          ? t("kudos_reason_too_short", { defaultValue: "Reason is too short" })
          : code === "SELF_KUDOS"
          ? t("kudos_self_forbidden", { defaultValue: "You cannot give kudos to yourself" })
          : code === "INVALID_AMOUNT"
          ? t("kudos_invalid_amount", { defaultValue: "Invalid amount" })
          : payload?.error || t("error");
      setSubmitError(message);
    }
  };

  const handleKey = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      handleSubmit();
    }
  };

  return (
    <div className="flex flex-col gap-3 min-w-0 md:min-w-[340px]">
      {/* pr-8 keeps the Balance block clear of the modal's floating close (X)
          button, which Polaris renders in this same top-right corner. */}
      <div className="flex items-center gap-3 pr-8">
        <UserAvatar companyId={companyId} user={toUser} size={48} rounded="lg" />
        <div>
          <div className="text-xs text-gray-500">
            {t("giving_kudos_to", { defaultValue: "Giving kudos to" })}
          </div>
          <div className="font-semibold">{toUser.name}</div>
        </div>
        <div className="ml-auto text-right">
          <div className="text-[11px] uppercase tracking-wide text-gray-500">
            {t("balance", { defaultValue: "Balance" })}
          </div>
          <div className={`font-semibold ${balance > 0 ? "text-green-700" : "text-gray-400"}`}>
            {balance}
          </div>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium mb-1">
          {t("kudos_amount", { defaultValue: "Amount" })}
        </label>
        <div className="flex items-center gap-1.5" role="radiogroup" aria-label={t("kudos_amount", { defaultValue: "Amount" })}>
          {[1, 2, 3, 4, 5].map((n) => {
            const disabled = n > balance;
            const selected = amount === n;
            return (
              <button
                key={n}
                type="button"
                role="radio"
                aria-checked={selected}
                disabled={disabled}
                onClick={() => setAmount(n)}
                className={
                  "w-11 h-11 md:w-10 md:h-10 rounded-md text-sm font-semibold border transition-colors " +
                  (selected
                    ? "bg-blue-600 text-white border-blue-600"
                    : disabled
                    ? "bg-gray-50 text-gray-300 border-gray-200 cursor-not-allowed"
                    : "bg-white text-gray-700 border-gray-300 hover:border-blue-500 hover:text-blue-600")
                }
              >
                {n}
              </button>
            );
          })}
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium mb-1">
          {t("kudos_reason_label", { defaultValue: "Why?" })}
        </label>
        <textarea
          autoFocus
          rows={4}
          maxLength={MAX_REASON + 20}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          onKeyDown={handleKey}
          placeholder={t("kudos_reason_placeholder", {
            defaultValue: "Helped me debug the API outage today, stayed late to push the fix.",
          })}
          className="w-full border border-gray-300 rounded-md p-2 text-base md:text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
        />
        <div className="flex justify-between text-[11px] mt-1">
          <span className={trimmedLen > 0 && trimmedLen < MIN_REASON ? "text-red-600" : "text-gray-500"}>
            {t("kudos_reason_min", { defaultValue: "At least 3 characters" })}
          </span>
          <span className={trimmedLen > MAX_REASON ? "text-red-600" : "text-gray-500"}>
            {trimmedLen}/{MAX_REASON}
          </span>
        </div>
      </div>

      {balance <= 0 && (
        <div className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-2">
          {t("kudos_no_balance_hint", {
            defaultValue: "You have no kudos left today. Balance refreshes every day.",
          })}
        </div>
      )}

      {submitError && (
        <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md p-2">
          {submitError}
        </div>
      )}

      <div className="flex justify-end gap-2">
        <Button size={isPhone ? "large" : undefined} onClick={() => dispatch(setIsModalxOpen(false))}>
          {t("cancel", { defaultValue: "Cancel" })}
        </Button>
        <Button
          size={isPhone ? "large" : undefined}
          variant="primary"
          loading={giving}
          disabled={!canSubmit}
          onClick={handleSubmit}
        >
          {t("send_kudos", { defaultValue: "Send kudos" })}
        </Button>
      </div>
    </div>
  );
};

export default GiveKudosModal;
